/**
 * What a doctor waited for, measured in the portal (STRATEGY.md 2026-10-02, pillar 2).
 *
 * Raw durations are buffered and posted about once a minute, and when the tab is hidden, to
 * `/api/v1/telemetry/client`. The server turns each name into a CloudWatch metric whose
 * percentiles CloudWatch computes. Only these names and numbers leave the browser — no study,
 * patient or user identifier — and the server refuses any other name.
 */

import { authorizedFetch } from './cognito';

export type TimingName = 'study_open_ms' | 'view_wait_ms' | 'marker_wait_ms' | 'verdict_ms';

const FLUSH_MS = 60_000;
const MAX_PER_NAME = 100;

const buffer = new Map<TimingName, number[]>();
let timer: ReturnType<typeof setInterval> | null = null;

function flush() {
  if (buffer.size === 0) return;
  const samples: Record<string, number[]> = {};
  buffer.forEach((values, name) => {
    samples[name] = values.splice(0, MAX_PER_NAME);
  });
  buffer.clear();
  // Fire and forget: losing one batch of timings costs nothing, and a failed post must never
  // surface as an error to someone reviewing a study.
  void authorizedFetch('/api/v1/telemetry/client', {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ samples }),
  }).catch(() => undefined);
}

function start() {
  if (timer !== null) return;
  timer = setInterval(flush, FLUSH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

/** Record one wait, in milliseconds. */
export function record(name: TimingName, ms: number) {
  if (!Number.isFinite(ms) || ms < 0) return;
  start();
  const values = buffer.get(name) ?? [];
  if (values.length < MAX_PER_NAME) values.push(Math.round(ms * 10) / 10);
  buffer.set(name, values);
}

/** Start a clock; calling the returned function records the elapsed time once. */
export function startTimer(name: TimingName): () => void {
  const t0 = performance.now();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    record(name, performance.now() - t0);
  };
}
